export default class Flyout {
	constructor() {
		this.node = document.querySelector('.flyout');

		if ( ! this.node ) {
			return;
		}

		this.classes = {
			wrap              : '.flyout__wrap',
			bodyOpened        : 'fixed',
			contentOpened     : 'flyout__content--opened',
			menuOpened        : 'flyout__submenu--opened',
			opened            : 'flyout--opened',
			toggleActive      : 'flyout__toggle--active',
			navHasToggleActive: 'flyout__nav--has-toggle-active',
			menus: {
				primary: '.flyout .nav--primary',
			},
			submenus          : {
				links: {
					level: ['nav__link--level-0', 'nav__link--level-1', 'nav__link--level-2'],
				},
				menus: {
					level: ['nav__menu--level-0', 'nav__menu--level-1', 'nav__menu--level-2'],
				}
			}
		};

		this.closer      = this.node.querySelector('.flyout__close');
		this.content     = this.node.querySelector('.flyout__content');
		this.modeToggle  = this.node.querySelector('.flyout__mode');
		this.nav         = this.node.querySelector('.flyout__nav');
		this.backs       = this.node.querySelectorAll('.flyout__back');
		this.toggles     = this.node.querySelectorAll('.flyout__toggle');
		this.searchInput = this.node.querySelector('input[name="s"]');

		this.modes = ['menu', 'search'];
		this.setMode('menu');

		this.initNavigation();

		if ( this.modeToggle ) {
			this.modeToggle.addEventListener('click', () => {
				if (this.mode === 'menu') {
					this.setMode('search');
					this.searchInput.focus();
				} else {
					this.setMode('menu');
				}
			});
		}

		if ( this.closer ) {
			this.closer.addEventListener('click', () =>  this.close());
		}

		if ( this.searchToggle ) {
			this.searchToggle.addEventListener('click', () => this.mode('search'));
		}

		// handle esc/tab keys
		this.handleKeyup = (e) => {
			if (this.opened) {
				if ( e.key === 'Escape' ) {
					return this.close();
				}

				if (e.key === 'Tab' && ! document.activeElement.closest('.flyout')) {
					// close the flyout when you tab out of it (if possible)
					return this.close();
				}
			}
		}

		// handle close
		this.node.addEventListener('click', (e) => {
			if (e.target.closest('.flyout__content')) {
				return;
			}

			this.close();
		});

		const openSubmenu = (navItem) => {
			if ( ! navItem) {
				return;
			}

			const submenu = navItem.querySelector('.flyout__submenu');
			const toggle  = navItem.querySelector('.flyout__toggle');

			this.nav.classList.add(this.classes.navHasToggleActive);
			toggle.classList.add(this.classes.toggleActive);
			submenu.classList.add(this.classes.menuOpened);
			submenu.style.height = `${submenu.scrollHeight}px`;

			submenu.querySelectorAll( 'a, button' ).forEach( node => {
				if ( node.dataset.tabIndex ) {
					node.tabIndex = node.dataset.tabIndex;
				}
			} );

			this.content.scrollTo(0,0);
		}

		const closeSubmenu = (navItem) => {
			if ( ! navItem ) {
				return;
			}

			const submenu = navItem.querySelector('.flyout__submenu');
			const toggle  = navItem.querySelector('.flyout__toggle');

			this.nav.classList.remove(this.classes.navHasToggleActive);
			toggle.classList.remove(this.classes.toggleActive);
			submenu.classList.remove(this.classes.menuOpened);
			submenu.style.height = '';

			submenu.querySelectorAll( 'a, button' ).forEach( node => {
				node.tabIndex = -1;
			} );

			this.content.scrollTo(0,0);
		}

		this.node.querySelectorAll('.flyout__submenu a, .flyout__submenu button').forEach( node => {
			node.dataset.tabIndex = node.tabIndex;
			node.tabIndex = -1;
		} );

		this.backs.forEach(back => {
			back.addEventListener('click', () => this.back());
		});

		this.toggles.forEach( toggle => {
			toggle.addEventListener('click', () => {
				const item = toggle.closest('.flyout__item');

				if (toggle.classList.contains(this.classes.toggleActive)) {
					// close
					closeSubmenu(item);
				} else {
					// open
					openSubmenu(item);
				}
			});
		});
	}

	open(mode) {
		if (mode) {
			this.setMode(mode);
		}

		this.opened = true;
		this.node.style.display = 'block';
		document.body.classList.add(this.classes.bodyOpened);
		document.addEventListener('keyup', this.handleKeyup);

		this.disableTabs();

		this.previousActiveElement = document.activeElement;

		requestAnimationFrame( () => requestAnimationFrame( () => {
			this.node.classList.add(this.classes.opened);
			this.content.classList.add(this.classes.contentOpened);

			if (this.mode === 'search' && this.searchInput) {
				this.searchInput.focus();
			} else if ( this.closer ) {
				this.closer.focus();
			}

			this.node.dispatchEvent( new CustomEvent('flyout:open', { bubbles: true, cancelable: false } ) );
		} ) );
	}

	close() {
		this.opened = false;
		document.removeEventListener('keyup', this.handleKeyup);

		const onTransitionEnd = (e) => {
			this.content.removeEventListener('transitionend', onTransitionEnd);

			this.node.style.display = '';

			document.body.classList.remove(this.classes.bodyOpened);
			this.node.classList.remove(this.classes.opened);

			if (this.previousActiveElement) {
				this.previousActiveElement.focus();
			}

			this.enableTabs();

			this.node.dispatchEvent( new CustomEvent('flyout:close', { bubbles: true, cancelable: false }) );
		}

		this.content.addEventListener('transitionend', onTransitionEnd);
		this.content.classList.remove(this.classes.contentOpened);

	}

	toggle() {
		this.opened ? this.closeSearch() : this.openSearch();
	}

	setMode( mode = 'menu' ) {
		if ( ! this.modes.includes( mode ) ) {
			return this.mode;
		}

		this.mode = mode;

		this.modes.forEach( m => {
			if ( mode === m ) {
				this.node.classList.add(`flyout--${m}`);
			} else {
				this.node.classList.remove(`flyout--${m}`);
			}
		} );

		return this.mode;
	}

	getTabs( scope = document ) {
		return Array.from(scope.querySelectorAll([
			'select',
			'input',
			'textarea',
			'button',
			'a',
			'iframe',
			'object',
			'embed',
			'*[contenteditable]',
			'*[tabindex]',
		].join(', '))).filter(node => {
			return ! node.closest('.flyout');
		});
	}

	disableTabs() {
		const tabs = this.getTabs();

		tabs.forEach(node => {
			let prevTabIndex = node.getAttribute('tabIndex');
			if (prevTabIndex) {
				node.setAttribute('data-tab-index', prevTabIndex);
			}

			node.setAttribute('tabIndex', '-1');
		});

		return tabs;
	}

	enableTabs() {
		const tabs = this.getTabs();

		tabs.forEach(node => {
			let prevTabIndex = node.getAttribute('data-tab-index');
			if (prevTabIndex) {
				node.setAttribute('tabIndex', prevTabIndex);
				node.removeAttribute('data-tab-index');
			} else {
				node.removeAttribute('tabIndex');
			}
		});

		return tabs;
	}

	initNavigation() {
		const { links, menus } = this.classes.submenus;
		const navs = [
			...this.node.getElementsByClassName(links.level[0]),
			...this.node.getElementsByClassName(links.level[1])
		];
		navs.map((link) => {
			link.addEventListener('click', (event) => {
				const submenu = [
					...link.parentNode.getElementsByClassName(menus.level[1]),
					...link.parentNode.getElementsByClassName(menus.level[2])
				];
				if (submenu.length > 0) {
	                // If there is a submenu, prevent default behavior
	                event.preventDefault();
	                submenu[0].classList.add('nav__menu--visible');
	                this.shift();
	            }
			});
		});
	}

	shift() {
		const wrap = document.querySelector(this.classes.wrap);
		wrap.classList.replace('nav--shifted', 'nav--shifted-again');
		wrap.classList.add('nav--shifted');
	}

	back() {
		const wrap = document.querySelector(this.classes.wrap);
		wrap.classList.remove('nav--shifted');
		wrap.classList.replace('nav--shifted-again', 'nav--shifted');
	}
}
